"use client";

import SectionHeader from "@/components/SectionHeader";
import { PATHS } from "@/constants/pathnames";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link } from "@/i18n/navigation";
import { formatCurrency } from "@/utils/currencyFormatter";

export default function RouteHighlights({
  cheapestRoutes,
  title,
  countryInformationPassedData,
  airlineInformationPassedData,
  airlineName,
  airlineSlug,
  airportSlug,
  disablePopularAirlineLink = false,
}) {
  const flightData = cheapestRoutes;
  const { currency } = useCurrency();
  const t = useTranslationDetector("common");
  // Fiyatı formatla
  const formatPrice = (price, currencyCode = currency) => {
    if (price === null || price === undefined || price === t("noData")) {
      return t("noData");
    }
    if (price === "") return t("noData");

    let numericPrice;
    if (typeof price === "number") {
      numericPrice = price;
    } else {
      numericPrice = parseFloat(price);
    }

    if (Number.isNaN(numericPrice)) return t("noData");

    // Currency kodunu düzelt (TL -> TRY)
    const normalizedCurrency = currencyCode === "TL" ? "TRY" : currencyCode;

    try {
      // Currency formatter kullan
      return formatCurrency(numericPrice, normalizedCurrency, {
        showSymbol: false,
        showCode: true,
        decimals: 0, // Tam sayı olarak göster
      });
    } catch (error) {
      console.error("formatCurrency error:", error);
      // Fallback: Basit formatlama
      return `${Math.round(numericPrice).toLocaleString("tr-TR")} TL`;
    }
  };

  // Veri kontrolü için helper fonksiyon
  const hasValidData = (value) => {
    return (
      value !== null &&
      value !== undefined &&
      value !== t("noData") &&
      value !== ""
    );
  };
  // airlineInformationPassedData varsa onu kullan, countryInformationPassedData varsa onu kullan, yoksa default değerleri kullan
  const allStatsData = airlineInformationPassedData
    ? airlineInformationPassedData
    : countryInformationPassedData
      ? [
          {
            title: t("cheapestFlightPrice"),
            value: formatPrice(
              countryInformationPassedData.Price,
              countryInformationPassedData?.CurrencyCode ||
                flightData?.Currency ||
                flightData?.CurrencyCode,
            ),
          },
          {
            title: t("cheapestMonth"),
            value: countryInformationPassedData.Month || t("noData"),
          },
          {
            title: t("popularAirline"),
            value:
              airlineName ||
              countryInformationPassedData?.AirlineName ||
              t("noData"),
            ...(!disablePopularAirlineLink
              ? airlineInformationPassedData?.Url
                ? { valueHref: airlineInformationPassedData?.Url }
                : airlineSlug
                  ? {
                      valueHref: {
                        pathname: PATHS.FLIGHT_TICKET_LANDING.PATH,
                        params: { landingPageName: airlineSlug },
                      },
                    }
                  : {}
              : {}),
          },
          {
            title: t("popularAirport"),
            value:
              flightData?.OriginAirportName && flightData?.OriginAirportCode
                ? `${flightData.OriginAirportName} (${flightData.OriginAirportCode})`
                : flightData?.OriginAirportName ||
                  flightData?.Origin?.Name ||
                  t("noData"),
            ...(airportSlug
              ? {
                  valueHref: {
                    pathname: PATHS.FLIGHT_TICKET_LANDING.PATH,
                    params: { landingPageName: airportSlug },
                  },
                }
              : {}),
          },
        ]
      : [
          {
            title: t("cheapestOneWayFlightPrice"),
            value: formatPrice(flightData?.Price, flightData?.Currency),
          },
          {
            title: t("averageFlightDuration"),
            value: flightData?.FlightDuration || t("noData"),
          },
          {
            title: t("popularAirline"),
            value: airlineName || t("noData"),
            ...(!disablePopularAirlineLink
              ? airlineInformationPassedData?.Url
                ? { valueHref: airlineInformationPassedData?.Url }
                : airlineSlug
                  ? {
                      valueHref: {
                        pathname: PATHS.FLIGHT_TICKET_LANDING.PATH,
                        params: { landingPageName: airlineSlug },
                      },
                    }
                  : {}
              : {}),
          },
          {
            title: t("popularAirport"),
            value:
              flightData?.OriginAirportName && flightData?.OriginAirportCode
                ? `${flightData.OriginAirportName} (${flightData.OriginAirportCode})`
                : flightData?.OriginAirportName ||
                  flightData?.Origin?.Name ||
                  t("noData"),
            ...(airportSlug
              ? {
                  valueHref: {
                    pathname: PATHS.FLIGHT_TICKET_LANDING.PATH,
                    params: { landingPageName: airportSlug },
                  },
                }
              : {}),
          },
        ];

  // Verisi olmayan başlıkları filtrele
  const statsData = allStatsData.filter((stat) => hasValidData(stat.value));
  // Eğer hiç veri yoksa section'ı gizle
  if (statsData.length === 0) {
    return null;
  }

  // Dinamik grid sınıfları oluştur
  const getGridClasses = (dataCount) => {
    // Mobil: 1 column, sm: 2 column, xl: tek satır (tüm elemanlar yan yana)
    const baseClasses =
      "divide-border-card grid grid-cols-1 divide-y border-border-card rounded-lg border";

    // xl için maksimum 6 sütun destekle (daha fazla eleman varsa 6 sütun kullan)
    const xlColumns = Math.min(dataCount, 7);

    // Responsive grid sınıfları
    let responsiveClasses =
      "sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:divide-x xl:divide-y-0";

    // xl için dinamik sütun sayısı
    if (xlColumns === 1) responsiveClasses += " xl:grid-cols-1";
    else if (xlColumns === 2) responsiveClasses += " xl:grid-cols-2";
    else if (xlColumns === 3) responsiveClasses += " xl:grid-cols-3";
    else if (xlColumns === 4) responsiveClasses += " xl:grid-cols-4";
    else if (xlColumns === 5) responsiveClasses += " xl:grid-cols-5";
    else if (xlColumns === 6) responsiveClasses += " xl:grid-cols-6";
    else if (xlColumns === 7) responsiveClasses += " xl:grid-cols-7";

    // Border düzenlemeleri
    const borderClasses = `sm:[&>*:nth-child(-n+2)]:border-b-0 xl:[&>*:nth-child(-n+${xlColumns})]:border-b-0`;

    return `${baseClasses} ${responsiveClasses} ${borderClasses}`;
  };

  return (
    <div className="flex flex-col gap-8">
      <SectionHeader title={title} />
      <div className="bg-background-global rounded-lg">
        <div className={getGridClasses(statsData.length)}>
          {statsData.map((stat, index) => (
            <Stat
              key={index}
              title={stat.title}
              value={stat.value}
              valueHref={stat.valueHref}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ title, value, valueHref }) {
  const valueClassName =
    "text-lg/26 font-semibold text-black underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

  return (
    <div className="flex flex-col gap-[5px] p-6 text-center lg:px-6 lg:py-10">
      <p className="text-lg/26 font-normal text-black">{title}</p>
      <div className="text-lg/26 font-semibold text-black">
        {valueHref ? (
          <Link href={valueHref} className={valueClassName}>
            {value}
          </Link>
        ) : (
          value
        )}
      </div>
    </div>
  );
}
